
export const CAMPAIGN_PRIORITY = {
  LOW: 0,
  NORMAL: 1,
  HIGH: 2,
  MAX: 3,
} as const;

export type CampaignPriority =
  (typeof CAMPAIGN_PRIORITY)[keyof typeof CAMPAIGN_PRIORITY];

export const DEFAULT_CAMPAIGN_PRIORITY: CampaignPriority =
  CAMPAIGN_PRIORITY.NORMAL;

export interface PriorityLevel {
  value: CampaignPriority;
  label: string;
  description: string;
  badgeClassName: string;
  selectedClassName: string;
  dotClassName: string;
}

export const PRIORITY_LEVELS: Record<CampaignPriority, PriorityLevel> = {
  [CAMPAIGN_PRIORITY.MAX]: {
    value: CAMPAIGN_PRIORITY.MAX,
    label: "Máxima",
    description: "Vence as demais do mesmo jogo no período",
    badgeClassName: "bg-red-100 text-red-800 border-red-200",
    selectedClassName: "border-red-500 bg-red-50",
    dotClassName: "bg-red-500",
  },
  [CAMPAIGN_PRIORITY.HIGH]: {
    value: CAMPAIGN_PRIORITY.HIGH,
    label: "Alta",
    description: "Vence as de prioridade Normal e Baixa",
    badgeClassName: "bg-orange-100 text-orange-800 border-orange-200",
    selectedClassName: "border-orange-500 bg-orange-50",
    dotClassName: "bg-orange-500",
  },
  [CAMPAIGN_PRIORITY.NORMAL]: {
    value: CAMPAIGN_PRIORITY.NORMAL,
    label: "Normal",
    description: "Padrão — vence só as de prioridade Baixa",
    badgeClassName: "bg-blue-100 text-blue-800 border-blue-200",
    selectedClassName: "border-blue-500 bg-blue-50",
    dotClassName: "bg-blue-500",
  },
  [CAMPAIGN_PRIORITY.LOW]: {
    value: CAMPAIGN_PRIORITY.LOW,
    label: "Baixa",
    description: "Só vale se nenhuma outra do mesmo jogo estiver no período",
    badgeClassName: "bg-slate-100 text-slate-700 border-slate-200",
    selectedClassName: "border-slate-500 bg-slate-50",
    dotClassName: "bg-slate-500",
  },
};

export const PRIORITY_LEVEL_ORDER: readonly CampaignPriority[] = [
  CAMPAIGN_PRIORITY.MAX,
  CAMPAIGN_PRIORITY.HIGH,
  CAMPAIGN_PRIORITY.NORMAL,
  CAMPAIGN_PRIORITY.LOW,
];

export const PRIORITY_RULE_HINT =
  "Entre campanhas do mesmo jogo no mesmo período, vale a de maior prioridade. " +
  "Em empate, vale a criada por último.";

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value !== "string" || value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function priorityLevelOf(value: unknown): CampaignPriority {
  const parsed = toFiniteNumber(value);
  if (parsed === undefined || parsed <= CAMPAIGN_PRIORITY.LOW) {
    return CAMPAIGN_PRIORITY.LOW;
  }
  if (parsed >= CAMPAIGN_PRIORITY.MAX) return CAMPAIGN_PRIORITY.MAX;
  return Math.floor(parsed) as CampaignPriority;
}

export function formPriorityLevelOf(value: unknown): CampaignPriority {
  if (toFiniteNumber(value) === undefined) return DEFAULT_CAMPAIGN_PRIORITY;
  return priorityLevelOf(value);
}
